export interface UserProfile {
  displayName?: string | null;
  locale?: string | null;
  theme?: string | null;
  /** Per-app prefs keyed by appID, e.g. { fileBrowser: { viewMode: 'icons' } } */
  userAppPreferences?: Record<string, Record<string, unknown>> | null;
}

export interface SessionUser {
  username: string;
  homePath?: string | null;
  smbHostRef?: string | null;
  profileLoaded?: boolean;
  userProfile?: UserProfile | null;
}

export interface LoginResponse {
  username: string;
  sessionToken: string;
  client?: string;
  expiresInMs?: number;
  homePath?: string | null;
  smbHostRef?: string | null;
  userProfile?: UserProfile | null;
}

/** True when the user has saved a Home, including SMB root `/`. */
export function hasChosenHome(user: SessionUser | null | undefined): boolean {
  return !!(user?.homePath && user.homePath.trim());
}

/** File Browser home: saved path (including `/`), or SMB root when unset. */
export function effectiveHomePath(user: SessionUser | null | undefined): string {
  if (user?.homePath && user.homePath.trim()) {
    return user.homePath;
  }
  return '/';
}

function appPreferencesOf(profile: UserProfile | null | undefined): Record<string, Record<string, unknown>> {
  const raw = profile?.userAppPreferences;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }
  return {};
}

/** Merge saved app prefs over defaults. Miniapps should read this from desktop sessionUser. */
export function preferenceForApp<T extends object>(
  user: SessionUser | null | undefined,
  appId: string,
  defaults: T
): T {
  const raw = appPreferencesOf(user?.userProfile)?.[appId];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...defaults };
  }
  return { ...defaults, ...(raw as Partial<T>) };
}

export function withAppPreference(
  user: SessionUser | null,
  appId: string,
  preference: Record<string, unknown>
): SessionUser | null {
  if (!user) {
    return user;
  }
  const profile: UserProfile = { ...(user.userProfile || {}) };
  profile.userAppPreferences = {
    ...appPreferencesOf(profile),
    [appId]: preference,
  };
  return { ...user, userProfile: profile, profileLoaded: true };
}
